import {
  AutomationType,
  TriggerType,
  TriggerTypeClient,
  UpdateRecordList,
} from 'helpers/enum';
import { liveFirst } from './helpers';

export function buildUpdateFieldGroups(updateOptions, automationType, triggerType, isBullhornProvider) {
  const isClient = automationType === AutomationType.Client;
  const isJobBased = triggerType === TriggerType.JobBased || triggerType === TriggerTypeClient.JobBased;
  const isPlacementBased = triggerType === TriggerType.PlacementBased || triggerType === TriggerTypeClient.PlacementBased;

  function findOption(id) {
    return updateOptions.find(o => o.id === id) || null;
  }

  // Candidate-only address targets; contacts have no equivalent, so callers add these on candidate groups only.
  function candidateAddressItems() {
    return [
      { id: UpdateRecordList.City, label: 'City' },
      { id: UpdateRecordList.State, label: 'State' },
      { id: UpdateRecordList.CandidatePostalCode, label: 'Postal code' },
      { id: UpdateRecordList.CandidateCountry, label: 'Country' },
    ].map(({ id, label }) => {
      const opt = findOption(id);
      return { label, status: opt ? 'live' : 'soon', data: opt };
    });
  }

  // A single live item for the given option, or none when this campaign type doesn't offer it.
  function liveItemFor(id) {
    const opt = findOption(id);
    return opt ? [{ label: opt.name, status: 'live', data: opt }] : [];
  }

  const customFieldItems = liveItemFor(UpdateRecordList.CustomFields);

  const explicitIds = [
    UpdateRecordList.Status,
    UpdateRecordList.AddNote,
    UpdateRecordList.CustomFields,
    UpdateRecordList.PlacementCustomFields,
    UpdateRecordList.JobCustomFields,
    UpdateRecordList.ApplicationCustomFields,
    UpdateRecordList.CandidateSkills,
    UpdateRecordList.Unsubscribe,
    UpdateRecordList.ApplicationStatus,
    UpdateRecordList.ApplicationRating,
    UpdateRecordList.AddToJob,
    UpdateRecordList.Rating,
    UpdateRecordList.City,
    UpdateRecordList.State,
    UpdateRecordList.PlacementStatus,
    UpdateRecordList.JobStatus,
    UpdateRecordList.CandidatePostalCode,
    UpdateRecordList.CandidateCountry,
  ];
  const extraCustomItems = updateOptions
    .filter(o => !explicitIds.includes(o.id) && o.id !== UpdateRecordList.AddNote)
    .map(o => ({ label: o.name, status: 'live', data: o }));

  const combinedCustom = [...customFieldItems, ...extraCustomItems];

  if (isJobBased) {
    const appStatusOpt = findOption(UpdateRecordList.ApplicationStatus);
    const statusOpt = findOption(UpdateRecordList.Status);
    const skillsOpt = findOption(UpdateRecordList.CandidateSkills);
    const jobStatusOpt = findOption(UpdateRecordList.JobStatus);
    const ratingOpt = findOption(UpdateRecordList.Rating);
    const unsubOpt = findOption(UpdateRecordList.Unsubscribe);
    // JobAdder only - the backend has no handling for this on other providers.
    const appRatingOpt = isBullhornProvider ? null : findOption(UpdateRecordList.ApplicationRating);

    const jobFieldItems = isClient ? [
      { label: 'Job status', status: jobStatusOpt ? 'live' : 'soon', data: jobStatusOpt },
      { label: 'Job skills', status: 'request', data: null },
      { label: 'Job end date', status: 'request', data: null },
      { label: 'Category', status: 'request', data: null },
      { label: 'Location', status: 'request', data: null },
      { label: 'Work type', status: 'request', data: null },
      { label: 'Job source', status: 'request', data: null },
    ] : [
      { label: 'Application status', status: appStatusOpt ? 'live' : 'soon', data: appStatusOpt },
      { label: 'Application rating', status: appRatingOpt ? 'live' : 'soon', data: appRatingOpt },
      { label: 'Job status', status: jobStatusOpt ? 'live' : 'soon', data: jobStatusOpt },
      { label: 'Job skills', status: 'request', data: null },
      { label: 'Job end date', status: 'request', data: null },
      { label: 'Category', status: 'request', data: null },
      { label: 'Location', status: 'request', data: null },
      { label: 'Work type', status: 'request', data: null },
      { label: 'Job source', status: 'request', data: null },
    ];

    const jobEntityFieldItems = [
      { label: 'Status', status: statusOpt ? 'live' : 'soon', data: statusOpt },
      { label: 'Skills', status: skillsOpt ? 'live' : 'soon', data: skillsOpt },
      { label: 'Rating', status: ratingOpt ? 'live' : 'soon', data: ratingOpt },
      { label: 'Unsubscribed', status: unsubOpt ? 'live' : 'soon', data: unsubOpt },
    ];

    if (isClient) {
      // Contact and Job custom fields each get their own section; application custom fields are
      // candidate-side only.
      const jobCustomFieldItems = liveItemFor(UpdateRecordList.JobCustomFields);
      return [
        { name: 'Job fields', items: liveFirst(jobFieldItems) },
        { name: 'Contact fields', items: liveFirst(jobEntityFieldItems) },
        ...(combinedCustom.length > 0 ? [{ name: 'Contact Custom Fields', items: combinedCustom }] : []),
        ...(jobCustomFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: jobCustomFieldItems }] : []),
      ];
    }

    // Candidate Job-based: Candidate, Job, and Application custom fields each get their own section,
    // in that order.
    const candidateCustomFieldOption = findOption(UpdateRecordList.CustomFields);
    const candidateCustomFieldItems = candidateCustomFieldOption
      ? [{ label: candidateCustomFieldOption.name, status: 'live', data: candidateCustomFieldOption }]
      : [];
    const jobCustomFieldOption = findOption(UpdateRecordList.JobCustomFields);
    const jobCustomFieldItems = jobCustomFieldOption
      ? [{ label: jobCustomFieldOption.name, status: 'live', data: jobCustomFieldOption }]
      : [];
    const applicationCustomFieldOption = findOption(UpdateRecordList.ApplicationCustomFields);
    const applicationCustomFieldItems = applicationCustomFieldOption
      ? [{ label: applicationCustomFieldOption.name, status: 'live', data: applicationCustomFieldOption }]
      : [];

    return [
      { name: 'Job fields', items: liveFirst(jobFieldItems) },
      { name: 'Candidate fields', items: liveFirst([...jobEntityFieldItems, ...candidateAddressItems()]) },
      ...(candidateCustomFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: candidateCustomFieldItems }] : []),
      ...(jobCustomFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: jobCustomFieldItems }] : []),
      ...(applicationCustomFieldItems.length > 0 ? [{ name: 'Application Custom Fields', items: applicationCustomFieldItems }] : []),
    ];
  }

  if (isPlacementBased) {
    const statusOpt = findOption(UpdateRecordList.Status);
    const skillsOpt = findOption(UpdateRecordList.CandidateSkills);
    const placementStatusOpt = findOption(UpdateRecordList.PlacementStatus);
    const ratingOpt = findOption(UpdateRecordList.Rating);
    const unsubOpt = findOption(UpdateRecordList.Unsubscribe);

    const placementFieldItems = [
      { label: 'Placement status', status: placementStatusOpt ? 'live' : 'soon', data: placementStatusOpt },
      { label: 'Start date / End date', status: 'request', data: null },
      { label: 'Payment type', status: 'request', data: null },
      { label: 'Salary / Rates', status: 'request', data: null },
      { label: 'Billing fields', status: 'request', data: null },
    ];

    const placementEntityFieldItems = [
      { label: 'Status', status: statusOpt ? 'live' : 'soon', data: statusOpt },
      { label: 'Skills', status: skillsOpt ? 'live' : 'soon', data: skillsOpt },
      { label: 'Rating', status: ratingOpt ? 'live' : 'soon', data: ratingOpt },
      { label: 'Unsubscribed', status: unsubOpt ? 'live' : 'soon', data: unsubOpt },
    ];

    if (isClient) {
      // Contact and Placement custom fields each get their own section.
      const placementCustomFieldItems = liveItemFor(UpdateRecordList.PlacementCustomFields);
      return [
        { name: 'Placement fields', items: liveFirst(placementFieldItems) },
        { name: 'Contact fields', items: liveFirst(placementEntityFieldItems) },
        ...(combinedCustom.length > 0 ? [{ name: 'Contact Custom Fields', items: combinedCustom }] : []),
        ...(placementCustomFieldItems.length > 0 ? [{ name: 'Placement Custom Fields', items: placementCustomFieldItems }] : []),
      ];
    }

    // Candidate Placement-based: Candidate, Placement, and Job custom fields each get their own section,
    // in that order, plus a Job fields section (same static fields shown for Job-based automations).
    const appStatusOpt = findOption(UpdateRecordList.ApplicationStatus);
    const jobStatusOpt = findOption(UpdateRecordList.JobStatus);
    const placementJobFieldItems = [
      { label: 'Application status', status: appStatusOpt ? 'live' : 'soon', data: appStatusOpt },
      { label: 'Job status', status: jobStatusOpt ? 'live' : 'soon', data: jobStatusOpt },
      { label: 'Job skills', status: 'request', data: null },
      { label: 'Job end date', status: 'request', data: null },
      { label: 'Category', status: 'request', data: null },
      { label: 'Location', status: 'request', data: null },
      { label: 'Work type', status: 'request', data: null },
      { label: 'Job source', status: 'request', data: null },
    ];

    const candidateCustomFieldOption = findOption(UpdateRecordList.CustomFields);
    const candidateCustomFieldItems = candidateCustomFieldOption
      ? [{ label: candidateCustomFieldOption.name, status: 'live', data: candidateCustomFieldOption }]
      : [];
    const placementCustomFieldOption = findOption(UpdateRecordList.PlacementCustomFields);
    const placementCustomFieldItems = placementCustomFieldOption
      ? [{ label: placementCustomFieldOption.name, status: 'live', data: placementCustomFieldOption }]
      : [];
    const jobCustomFieldOption = findOption(UpdateRecordList.JobCustomFields);
    const jobCustomFieldItems = jobCustomFieldOption
      ? [{ label: jobCustomFieldOption.name, status: 'live', data: jobCustomFieldOption }]
      : [];

    return [
      { name: 'Placement fields', items: liveFirst(placementFieldItems) },
      { name: 'Job fields', items: liveFirst(placementJobFieldItems) },
      { name: 'Candidate fields', items: liveFirst([...placementEntityFieldItems, ...candidateAddressItems()]) },
      ...(candidateCustomFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: candidateCustomFieldItems }] : []),
      ...(placementCustomFieldItems.length > 0 ? [{ name: 'Placement Custom Fields', items: placementCustomFieldItems }] : []),
      ...(jobCustomFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: jobCustomFieldItems }] : []),
    ];
  }

  if (isClient) {
    const statusOpt = findOption(UpdateRecordList.Status);
    const skillsOpt =
      updateOptions.find(o => o.name && o.name.toLowerCase() === 'skills') ||
      findOption(UpdateRecordList.CandidateSkills);
    const unsubOpt = findOption(UpdateRecordList.Unsubscribe);

    return [
      {
        name: 'Contact fields',
        items: liveFirst([
          { label: 'Status', status: statusOpt ? 'live' : 'soon', data: statusOpt },
          { label: 'Skills', status: skillsOpt ? 'live' : 'soon', data: skillsOpt },
          { label: 'Unsubscribed', status: unsubOpt ? 'live' : 'soon', data: unsubOpt },
        ]),
      },
      ...(combinedCustom.length > 0 ? [{ name: 'Contact Custom Fields', items: combinedCustom }] : []),
    ];
  }

  // Candidate (candidate-based)
  const statusOpt = findOption(UpdateRecordList.Status);
  const skillsOpt = findOption(UpdateRecordList.CandidateSkills);
  const unsubOpt = findOption(UpdateRecordList.Unsubscribe);
  const ratingOpt = findOption(UpdateRecordList.Rating);
  const cityOpt = findOption(UpdateRecordList.City);
  const stateOpt = findOption(UpdateRecordList.State);
  const postalCodeOpt = findOption(UpdateRecordList.CandidatePostalCode);
  const countryOpt = findOption(UpdateRecordList.CandidateCountry);

  return [
    {
      name: 'Candidate fields',
      items: liveFirst([
        { label: 'Status', status: statusOpt ? 'live' : 'soon', data: statusOpt },
        { label: 'Skills', status: skillsOpt ? 'live' : 'soon', data: skillsOpt },
        { label: 'Rating', status: ratingOpt ? 'live' : 'soon', data: ratingOpt },
        { label: 'City', status: cityOpt ? 'live' : 'soon', data: cityOpt },
        { label: 'State', status: stateOpt ? 'live' : 'soon', data: stateOpt },
        { label: 'Postal code', status: postalCodeOpt ? 'live' : 'soon', data: postalCodeOpt },
        { label: 'Country', status: countryOpt ? 'live' : 'soon', data: countryOpt },
        { label: 'Unsubscribed', status: unsubOpt ? 'live' : 'soon', data: unsubOpt },
      ]),
    },
    ...(combinedCustom.length > 0 ? [{ name: 'Candidate Custom Fields', items: combinedCustom }] : []),
  ];
}
